import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  InputAdornment,
  Switch,
  Badge,
  Tooltip,
  CardMedia,
  CardActions,
  Divider,
  Stack,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ptBR from 'date-fns/locale/pt-BR';
import { 
  AutoFixHigh as AutoFixHighIcon,
  PhotoCamera as PhotoCameraIcon,
  Videocam as VideocamIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Remove as RemoveIcon,
  CloudUpload as CloudUploadIcon,
  Send as SendIcon,
  Campaign as CampaignIcon,
  People as PeopleIcon,
  SmartToy as SmartToyIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  YouTube as YouTubeIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import * as api from '../services/api';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { isSameDay, format } from 'date-fns';
import RichTextEditor from './RichTextEditor';
import { useCreateNotification } from '../hooks/useCreateNotification';
import { fetchProductsAsync } from '../redux/slices/productSlice';
import { useNotifications } from '../contexts/NotificationContext';

interface Schedule {
  startDate: Date;
  endDate: Date;
  repeat?: {
    enabled: boolean;
    frequency: 'daily' | 'weekly' | 'monthly';
    interval: number;
    endAfter?: Date;
    customTimes?: Date[];
  };
}

interface Post {
  id: string;
  content: string;
  platforms: string[];
  platformTypes: {
    [key: string]: string[]; // Agora é um array de strings para permitir múltiplos tipos
  };
  schedules: Schedule[];
  imageUrl?: string;
  status: 'agendado' | 'publicado' | 'falha';
  mediaFiles?: File[];
  platformStatus: {
    [key: string]: {
      status: 'agendado' | 'publicado' | 'falha';
      publishedAt?: Date;
      error?: string;
      whatsappNumber?: string;
    };
  };
  platformMedia: {
    [key: string]: File;
  };
  product?: {
    id: string;
    name: string;
    price: number;
    description?: string;
    imageUrl?: string;
  };
  createdAt: Date;
}

interface MediaFile {
  id: string;
  file: File;
  type: 'image' | 'video';
  preview: string;
}

interface MediaPreview {
  file: File;
  preview: string;
  type: 'image' | 'video';
}

interface Lead {
  id: string;
  name: string;
  email: string;
  source: string;
  status: string;
}

interface SelectedDate {
  date: Date;
  times: Date[];
}

interface MediaDimensions {
  width: number;
  height: number;
}

const SOCIAL_MEDIA_DIMENSIONS = {
  instagram: {
    square: { width: 1080, height: 1080 },
    horizontal: { width: 1080, height: 566 },
    vertical: { width: 1080, height: 1350 },
    stories: { width: 1080, height: 1920 }
  },
  facebook: {
    timeline: { width: 1200, height: 630 },
    cover: { width: 851, height: 315 }
  },
  linkedin: {
    cover: { width: 1584, height: 396 }
  },
  tiktok: {
    video: { width: 1080, height: 1920 }
  },
  whatsapp: {
    profile: { width: 192, height: 192 },
    businessCover: { width: 590, height: 340 },
    conversation: { width: 1200, height: 800 }
  }
};

const validateMediaDimensions = async (file: File, platforms: string[]): Promise<{ isValid: boolean; message: string }> => {
  return new Promise((resolve) => {
    if (file.type.startsWith('image/')) {
      const img = new Image();
      img.onload = () => {
        const dimensions = { width: img.width, height: img.height };
        let isValid = true;
        let message = '';

        platforms.forEach(platform => {
          switch (platform.toLowerCase()) {
            case 'instagram':
              if (!isValidInstagramDimensions(dimensions)) {
                isValid = false;
                message = 'Dimensões inválidas para Instagram. Use: 1080x1080 (quadrado), 1080x566 (horizontal), 1080x1350 (vertical) ou 1080x1920 (stories)';
              }
              break;
            case 'facebook':
              if (!isValidFacebookDimensions(dimensions)) {
                isValid = false;
                message = 'Dimensões inválidas para Facebook. Use: 1200x630 (timeline) ou 851x315 (capa)';
              }
              break;
            case 'linkedin':
              if (!isValidLinkedInDimensions(dimensions)) {
                isValid = false;
                message = 'Dimensões inválidas para LinkedIn. Use: 1584x396 (capa)';
              }
              break;
            case 'whatsapp':
              if (!isValidWhatsAppDimensions(dimensions)) {
                isValid = false;
                message = 'Dimensões inválidas para WhatsApp. Use: 192x192 (perfil), 590x340 (capa business) ou 1200x800 (conversas)';
              }
              break;
          }
        });

        resolve({ isValid, message });
      };
      img.src = URL.createObjectURL(file);
    } else if (file.type.startsWith('video/')) {
      const video = document.createElement('video');
      video.onloadedmetadata = () => {
        const dimensions = { width: video.videoWidth, height: video.videoHeight };
        let isValid = true;
        let message = '';

        platforms.forEach(platform => {
          switch (platform.toLowerCase()) {
            case 'instagram':
              if (!isValidInstagramVideoDimensions(dimensions)) {
                isValid = false;
                message = 'Dimensões inválidas para vídeo no Instagram. Use: 1080x1080 (quadrado), 1080x566 (paisagem) ou 1080x1350 (retrato)';
              }
              break;
            case 'tiktok':
              if (!isValidTikTokDimensions(dimensions)) {
                isValid = false;
                message = 'Dimensões inválidas para TikTok. Use: 1080x1920 (proporção 9:16)';
              }
              break;
          }
        });

        resolve({ isValid, message });
      };
      video.src = URL.createObjectURL(file);
    } else {
      resolve({ isValid: false, message: 'Tipo de arquivo não suportado' });
    }
  });
};

const isValidInstagramDimensions = (dimensions: MediaDimensions): boolean => {
  const { width, height } = dimensions;
  return (
    (width === 1080 && height === 1080) || // quadrado
    (width === 1080 && height === 566) || // horizontal
    (width === 1080 && height === 1350) || // vertical
    (width === 1080 && height === 1920) // stories
  );
};

const isValidInstagramVideoDimensions = (dimensions: MediaDimensions): boolean => {
  const { width, height } = dimensions;
  return (
    (width === 1080 && height === 1080) || // quadrado
    (width === 1080 && height === 566) || // paisagem
    (width === 1080 && height === 1350) // retrato
  );
};

const isValidFacebookDimensions = (dimensions: MediaDimensions): boolean => {
  const { width, height } = dimensions;
  return (
    (width === 1200 && height === 630) || // timeline
    (width === 851 && height === 315) // capa
  );
};

const isValidLinkedInDimensions = (dimensions: MediaDimensions): boolean => {
  const { width, height } = dimensions;
  return width === 1584 && height === 396; // capa
};

const isValidTikTokDimensions = (dimensions: MediaDimensions): boolean => {
  const { width, height } = dimensions;
  return width === 1080 && height === 1920; // proporção 9:16
};

const isValidWhatsAppDimensions = (dimensions: MediaDimensions): boolean => {
  const { width, height } = dimensions;
  return (
    (width === 192 && height === 192) || // perfil
    (width === 590 && height === 340) || // capa business
    (width === 1200 && height === 800) // conversas
  );
};

const getPlatformDimensions = (platform: string): { width: number; height: number } => {
  switch (platform.toLowerCase()) {
    case 'instagram':
      return { width: 1080, height: 1080 }; // formato quadrado
    case 'facebook':
      return { width: 1200, height: 630 }; // formato timeline
    case 'linkedin':
      return { width: 1200, height: 627 }; // formato timeline
    case 'tiktok':
      return { width: 1080, height: 1920 }; // formato vertical
    default:
      return { width: 1080, height: 1080 }; // formato padrão
  }
};

const getPlatformPreviewStyle = (platform: string) => {
  switch (platform.toLowerCase()) {
    case 'instagram':
      return {
        width: '350px',
        height: '350px',
        borderRadius: '8px',
        backgroundColor: '#ffffff',
        border: '1px solid #dbdbdb'
      };
    case 'facebook':
      return {
        width: '400px',
        height: '210px',
        borderRadius: '8px',
        backgroundColor: '#ffffff',
        border: '1px solid #dddfe2'
      };
    case 'twitter':
      return {
        width: '380px',
        height: '200px',
        borderRadius: '12px',
        backgroundColor: '#ffffff',
        border: '1px solid #ebeef0'
      };
    case 'tiktok':
      return {
        width: '250px',
        height: '445px',
        borderRadius: '8px',
        backgroundColor: '#000000',
        border: '1px solid #333333'
      };
    default:
      return {
        width: '350px',
        height: '350px',
        borderRadius: '8px',
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0'
      };
  }
};

const getPlatformHeaderStyle = (platform: string) => {
  switch (platform.toLowerCase()) {
    case 'instagram':
      return {
        height: '54px',
        padding: '8px',
        borderBottom: '1px solid #dbdbdb',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      };
    case 'facebook':
      return {
        height: '40px',
        padding: '8px',
        borderBottom: '1px solid #dddfe2',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      };
    case 'twitter':
      return {
        height: '48px',
        padding: '12px',
        borderBottom: '1px solid #ebeef0',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      };
    case 'tiktok':
      return {
        height: '48px',
        padding: '8px',
        borderBottom: '1px solid #333333',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: '#ffffff'
      };
    default:
      return {
        height: '48px',
        padding: '8px',
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      };
  }
};

const resizeMedia = async (file: File, platform: string): Promise<File> => {
  return new Promise((resolve, reject) => {
    const dimensions = getPlatformDimensions(platform);
    
    if (file.type.startsWith('image/')) {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Não foi possível criar o contexto do canvas'));
          return;
        }

        // Calcula as dimensões mantendo a proporção
        let width = img.width;
        let height = img.height;
        const ratio = width / height;

        switch (platform.toLowerCase()) {
          case 'instagram':
            // Instagram: 1080x1080 (quadrado)
            width = 1080;
            height = 1080;
            break;
          case 'facebook':
            // Facebook: 1200x630 (timeline)
            width = 1200;
            height = 630;
            break;
          case 'linkedin':
            // LinkedIn: 1200x627 (timeline)
            width = 1200;
            height = 627;
            break;
          case 'tiktok':
            // TikTok: 1080x1920 (proporção 9:16)
            width = 1080;
            height = 1920;
            break;
          default:
            // Dimensões padrão
            width = dimensions.width;
            height = dimensions.height;
        }

        canvas.width = width;
        canvas.height = height;

        // Configura o contexto para melhor qualidade
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Desenha a imagem redimensionada
        ctx.drawImage(img, 0, 0, width, height);

        // Converte para blob e depois para File
        canvas.toBlob((blob) => {
          if (blob) {
            const resizedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            });
            resolve(resizedFile);
          } else {
            reject(new Error('Falha ao criar o arquivo redimensionado'));
          }
        }, file.type, 0.95); // Qualidade de 95% para melhor compressão
      };
      img.onerror = () => reject(new Error('Erro ao carregar a imagem'));
      img.src = URL.createObjectURL(file);
    } else if (file.type.startsWith('video/')) {
      // Para vídeos, apenas valida as dimensões
      const video = document.createElement('video');
      video.onloadedmetadata = () => {
        const videoDimensions = { width: video.videoWidth, height: video.videoHeight };
        validateMediaDimensions(file, [platform]).then(result => {
          if (!result.isValid) {
            reject(new Error(result.message));
          } else {
            resolve(file);
          }
        });
      };
      video.onerror = () => reject(new Error('Erro ao carregar o vídeo'));
      video.src = URL.createObjectURL(file);
    } else {
      reject(new Error('Tipo de arquivo não suportado'));
    }
  });
};

interface Props {
  defaultPlatforms?: string[];
  disabledPlatforms?: string[];
  showPlatformSelector?: boolean;
  showPostForm?: boolean;
  showPostHistory?: boolean;
}

const SocialPostScheduler: React.FC<Props> = ({
  defaultPlatforms = [],
  disabledPlatforms = [],
  showPlatformSelector = true,
  showPostForm = true,
  showPostHistory = true
}) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [newPost, setNewPost] = useState<Omit<Post, 'id' | 'status'>>({
    content: '',
    platforms: defaultPlatforms,
    platformTypes: {},
    schedules: [{ startDate: new Date(), endDate: new Date() }],
    imageUrl: '',
    mediaFiles: [],
    platformStatus: {},
    platformMedia: {},
    product: undefined,
    createdAt: new Date()
  });
  const [isImprovingText, setIsImprovingText] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const dispatch = useAppDispatch();
  const products = useSelector((state: RootState) => state.products.items);
  const productsLoading = useSelector((state: RootState) => state.products.loading);
  const [productSearch, setProductSearch] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewPost, setPreviewPost] = useState<Post | null>(null);
  const [openDateDialog, setOpenDateDialog] = useState(false);
  const [repeatEnabled, setRepeatEnabled] = useState(false);
  const [repeatFrequency, setRepeatFrequency] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [repeatInterval, setRepeatInterval] = useState(1);
  const [repeatEndAfter, setRepeatEndAfter] = useState<Date | null>(null);
  const [customTimes, setCustomTimes] = useState<Date[]>([]);
  const [selectedDates, setSelectedDates] = useState<SelectedDate[]>([]);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [selectedDateForTime, setSelectedDateForTime] = useState<Date | null>(null);
  const { notifications, createNotification } = useNotifications();

  // Carregar postagens do localStorage quando o componente montar
  useEffect(() => {
    const loadPosts = () => {
      try {
        const savedPosts = localStorage.getItem('socialPosts');
        if (savedPosts) {
          // Converte as strings JSON para objetos
          const parsedPosts = JSON.parse(savedPosts);
          
          // Converte as strings de data para objetos Date
          const formattedPosts = parsedPosts.map((post: any) => {
            // Garante que schedules seja um array
            const schedules = Array.isArray(post.schedules) ? post.schedules : [];
            
            return {
              ...post,
              // Converte as datas de agendamento
              schedules: schedules.map((schedule: any) => ({
                ...schedule,
                startDate: new Date(schedule.startDate),
                endDate: new Date(schedule.endDate)
              })),
              // Converte a data de criação
              createdAt: new Date(post.createdAt || Date.now())
            };
          });
          
          // Remover duplicatas com base no ID
          const uniquePosts = formattedPosts.reduce((acc: Post[], current: Post) => {
            const existingPost = acc.find(post => post.id === current.id);
            if (!existingPost) {
              acc.push(current);
            }
            return acc;
          }, []);
          
          setPosts(uniquePosts);
          console.log(`Carregadas ${uniquePosts.length} postagens únicas do localStorage`);
        }
      } catch (error) {
        console.error('Erro ao carregar postagens:', error);
      }
    };

    // Carrega as postagens iniciais
    loadPosts();

    // Adiciona um listener para atualizar quando o evento customizado for disparado
    const handlePostsUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<Post[]>;
      if (customEvent.detail) {
        // Remover duplicatas antes de atualizar o estado
        const uniquePosts = customEvent.detail.reduce((acc: Post[], current: Post) => {
          const existingPost = acc.find(post => post.id === current.id);
          if (!existingPost) {
            acc.push(current);
          }
          return acc;
        }, []);
        
        setPosts(uniquePosts);
      }
    };

    window.addEventListener('socialPostsUpdated', handlePostsUpdated);

    // Limpa o listener quando o componente for desmontado
    return () => {
      window.removeEventListener('socialPostsUpdated', handlePostsUpdated);
    };
  }, []);

  // Salvar postagens no localStorage sempre que houver mudanças
  useEffect(() => {
    if (posts.length > 0) {
      try {
        // Prepara os dados para serialização
        const postsToSave = posts.map(post => ({
          ...post,
          schedules: post.schedules.map(schedule => ({
            ...schedule,
            startDate: schedule.startDate instanceof Date ? schedule.startDate.toISOString() : schedule.startDate,
            endDate: schedule.endDate instanceof Date ? schedule.endDate.toISOString() : schedule.endDate
          })),
          createdAt: post.createdAt instanceof Date ? post.createdAt.toISOString() : post.createdAt
        }));
        
        // Salva no localStorage
        localStorage.setItem('socialPosts', JSON.stringify(postsToSave));
        
        // Dispara o evento customizado
        window.dispatchEvent(new CustomEvent('socialPostsUpdated', { detail: posts }));
      } catch (error) {
        console.error('Erro ao salvar no localStorage:', error);
      }
    }
  }, [posts]);

  // Carregar produtos quando o componente montar
  useEffect(() => {
    // Verifica se já existem produtos carregados
    if (products.length === 0) {
      console.log('Carregando produtos do mockData service...');
      dispatch(fetchProductsAsync());
    } else {
      console.log('Produtos já carregados:', products.length);
    }
  }, [dispatch, products.length]);

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    (product.description && product.description.toLowerCase().includes(productSearch.toLowerCase()))
  );

  const handlePlatformChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    const selectedPlatforms = typeof value === 'string' ? value.split(',') : value;
    
    // Mantém as opções existentes para cada plataforma
    const updatedPlatformTypes = { ...newPost.platformTypes };
    
    // Para cada plataforma selecionada, mantém as opções existentes ou inicializa com valores padrão
    selectedPlatforms.forEach(platform => {
      if (!updatedPlatformTypes[platform]) {
        updatedPlatformTypes[platform] = platform === 'Facebook' ? ['feed'] : ['default'];
      }
    });
    
    // Remove as plataformas que não estão mais selecionadas
    Object.keys(updatedPlatformTypes).forEach(platform => {
      if (!selectedPlatforms.includes(platform)) {
        delete updatedPlatformTypes[platform];
      }
    });
    
    setNewPost({
      ...newPost,
      platforms: selectedPlatforms,
      platformTypes: updatedPlatformTypes
    });
  };

  const handlePlatformTypeChange = (platform: string, type: string) => {
    setNewPost(prev => {
      const currentTypes = prev.platformTypes[platform] || [];
      const newTypes = currentTypes.includes(type)
        ? currentTypes.filter(t => t !== type)
        : [...currentTypes, type];

      return {
        ...prev,
        platformTypes: {
          ...prev.platformTypes,
          [platform]: newTypes
        }
      };
    });
  };

  const handleDateChange = (date: Date | null, type: 'start' | 'end', index: number) => {
    if (date) {
      setNewPost(prev => ({
        ...prev,
        schedules: prev.schedules.map((schedule, i) => 
          i === index 
            ? { ...schedule, [type === 'start' ? 'startDate' : 'endDate']: date }
            : schedule
        )
      }));
    }
  };

  const handleAddSchedule = () => {
    setNewPost(prev => ({
      ...prev,
      schedules: [...prev.schedules, { startDate: new Date(), endDate: new Date() }]
    }));
  };

  const handleRemoveSchedule = (index: number) => {
    setNewPost(prev => ({
      ...prev,
      schedules: prev.schedules.filter((_, i) => i !== index)
    }));
  };

  const handleMediaUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Pega apenas o primeiro arquivo
    const file = files[0];
    
    // Limpa as mídias anteriores
    mediaFiles.forEach(media => {
      URL.revokeObjectURL(media.preview);
    });

    const newMedia: MediaFile = {
      id: Math.random().toString(36).substr(2, 9),
      file,
      type: file.type.startsWith('image/') ? 'image' : 'video',
      preview: URL.createObjectURL(file)
    };

    setMediaFiles([newMedia]);
    setNewPost(prev => ({
      ...prev,
      mediaFiles: [file]
    }));
  };

  const handleRemoveMedia = (id: string) => {
    setMediaFiles(prev => {
      const fileToRemove = prev.find(m => m.id === id);
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter(m => m.id !== id);
    });
    setNewPost(prev => ({
      ...prev,
      mediaFiles: (prev.mediaFiles || []).filter((_, index) => 
        index !== mediaFiles.findIndex(m => m.id === id)
      )
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Verifica se há conteúdo e plataformas selecionadas
      if (!newPost.content || newPost.platforms.length === 0) {
        setSnackbarMessage('Por favor, adicione conteúdo e selecione pelo menos uma plataforma.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      // Verifica se há agendamentos
      if (!newPost.schedules || newPost.schedules.length === 0) {
        setSnackbarMessage('Por favor, adicione pelo menos um agendamento.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      // Cria o objeto de postagem
      const post: Post = {
        id: Date.now().toString(),
        content: newPost.content,
        platforms: newPost.platforms,
        platformTypes: newPost.platformTypes,
        schedules: newPost.schedules.map(schedule => ({
          ...schedule,
          startDate: ensureDate(schedule.startDate),
          endDate: ensureDate(schedule.endDate)
        })),
        imageUrl: newPost.imageUrl,
        status: 'agendado',
        mediaFiles: newPost.mediaFiles ? [newPost.mediaFiles[0]] : [],
        platformStatus: newPost.platforms.reduce((acc, platform) => ({
          ...acc,
          [platform]: {
            status: 'agendado'
          }
        }), {}),
        platformMedia: newPost.platforms.reduce((acc, platform) => ({
          ...acc,
          [platform]: newPost.mediaFiles?.[0]
        }), {}),
        product: newPost.product,
        createdAt: new Date()
      };

      // Verifica se já existe uma postagem com o mesmo ID e remove antes de adicionar
      const filteredPosts = posts.filter(p => p.id !== post.id);
      
      // Adiciona a nova postagem à lista de postagens
      const updatedPosts = [...filteredPosts, post];
      
      // Atualiza o estado
      setPosts(updatedPosts);
      
      // Cria uma notificação para a página de notificações
      createNotification({
        title: 'Nova postagem agendada',
        message: `Postagem agendada para ${format(post.schedules[0].startDate, 'dd/MM/yyyy HH:mm')}`,
        type: 'success',
      });
      
      // Dispara o evento customizado para notificar outros componentes
      window.dispatchEvent(new CustomEvent('socialPostsUpdated', { detail: updatedPosts }));

      // Limpa o formulário
      setNewPost({
        content: '',
        platforms: defaultPlatforms,
        platformTypes: {},
        schedules: [{ startDate: new Date(), endDate: new Date() }],
        imageUrl: '',
        mediaFiles: [],
        platformStatus: {},
        platformMedia: {},
        product: undefined,
        createdAt: new Date()
      });
      setMediaFiles([]);
      setSelectedDates([]);
      setShowPreview(true);
      setPreviewPost(post);
      setOpenDateDialog(false);

      setSnackbarMessage('Postagem agendada com sucesso!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Erro ao agendar postagem:', error);
      setSnackbarMessage('Erro ao agendar postagem. Tente novamente.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const getStatusColor = (status: Post['status']) => {
    switch (status) {
      case 'agendado':
        return 'info';
      case 'publicado':
        return 'success';
      case 'falha':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPlatformStatusColor = (status: string) => {
    switch (status) {
      case 'agendado':
        return 'info';
      case 'publicado':
        return 'success';
      case 'falha':
        return 'error';
      default:
        return 'default';
    }
  };

  const renderPlatformStatus = (platform: string, status: any) => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          label={status.status}
          size="small"
          color={getPlatformStatusColor(status.status) as any}
          icon={
            status.status === 'agendado' ? <ScheduleIcon /> :
            status.status === 'publicado' ? <CheckCircleIcon /> :
            <ErrorIcon />
          }
        />
        {status.publishedAt && (
          <Typography variant="caption" color="text.secondary">
            {format(new Date(status.publishedAt), 'dd/MM/yyyy HH:mm')}
          </Typography>
        )}
        {status.error && (
          <Tooltip title={status.error}>
            <IconButton size="small" color="error">
              <ErrorIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  const renderPostCard = (post: Post) => {
    return (
      <Grid item xs={12} sm={6} md={4} key={post.id}>
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  {post.content.substring(0, 100)}...
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {post.platforms.map(platform => (
                    <Chip
                      key={platform}
                      label={platform}
                      size="small"
                      icon={
                        platform === 'Instagram' ? <InstagramIcon /> :
                        platform === 'Facebook' ? <FacebookIcon /> :
                        platform === 'TikTok' ? <SmartToyIcon /> :
                        platform === 'YouTube' ? <YouTubeIcon /> :
                        <ShareIcon />
                      }
                    />
                  ))}
                </Box>
              </Box>
              <Chip
                label={post.status}
                color={getStatusColor(post.status) as any}
                size="small"
              />
            </Box>

            {/* Mini card do produto */}
            {post.product && (
              <Box sx={{ mb: 2, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  {post.product.imageUrl && (
                    <Box 
                      sx={{ 
                        width: 120, 
                        height: 120, 
                        borderRadius: 1,
                        overflow: 'hidden',
                        flexShrink: 0,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    >
                      <Box
                        component="img"
                        src={post.product.imageUrl}
                        alt={post.product.name}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </Box>
                  )}
                  <Box sx={{ flex: 1, minWidth: 0, py: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" noWrap sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {post.product.name}
                      </Typography>
                      <Chip 
                        label="Produto" 
                        size="small" 
                        color="secondary" 
                        variant="outlined"
                        sx={{ flexShrink: 0, ml: 1 }}
                      />
                    </Box>
                    <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 'medium', mb: 1 }}>
                      R$ {post.product.price.toFixed(2)}
                    </Typography>
                    {post.product.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {post.product.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Status por Plataforma
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {Object.entries(post.platformStatus).map(([platform, status]) => (
                    <Box key={platform} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {platform}
                      </Typography>
                      {renderPlatformStatus(platform, status)}
                    </Box>
                  ))}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Agendamentos
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {post.schedules.map((schedule, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">
                        {format(new Date(schedule.startDate), 'dd/MM/yyyy HH:mm')}
                      </Typography>
                      {schedule.repeat && (
                        <Chip
                          label={`Repetir: ${schedule.repeat.frequency}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
          <CardActions>
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => handleEditPost(post)}
            >
              Editar
            </Button>
            <Button
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => handleDeletePost(post.id)}
            >
              Excluir
            </Button>
          </CardActions>
        </Card>
      </Grid>
    );
  };

  const handleImproveTextWithAI = async () => {
    if (!newPost.content) {
      setSnackbarMessage('Por favor, escreva um conteúdo antes de melhorá-lo com IA.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    setIsImprovingText(true);
    
    try {
      const response = await api.improveTextWithAI(newPost.content, 'social');
      
      if (response.data && response.data.improvedText) {
        setNewPost({
          ...newPost,
          content: response.data.improvedText,
        });
        setSnackbarMessage('Texto melhorado com sucesso!');
        setSnackbarSeverity('success');
      } else {
        throw new Error('Resposta inválida da API');
      }
    } catch (error) {
      console.error('Erro ao melhorar texto com IA:', error);
      setSnackbarMessage('Não foi possível melhorar o texto. Tente novamente mais tarde.');
      setSnackbarSeverity('error');
    } finally {
      setIsImprovingText(false);
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleSelectProduct = async (product: any) => {
    const productText = `🛍️ ${product.name}\n💰 R$ ${product.price.toFixed(2)}\n📝 ${product.description || ''}\n`;
    setNewPost(prev => ({
      ...prev,
      content: prev.content ? `${prev.content}\n\n${productText}` : productText,
      product: {
        id: product.id,
        name: product.name,
        price: product.price,
        description: product.description,
        imageUrl: product.imageUrl
      }
    }));

    // Adiciona a imagem do produto à mídia se existir
    if (product.imageUrl) {
      try {
        const response = await fetch(product.imageUrl);
        const blob = await response.blob();
        const file = new File([blob], `produto-${product.id}.jpg`, { type: 'image/jpeg' });
        
        const newMedia: MediaFile = {
          id: Math.random().toString(36).substr(2, 9),
          file,
          type: 'image',
          preview: product.imageUrl
        };

        setMediaFiles(prev => [...prev, newMedia]);
        setNewPost(prev => ({
          ...prev,
          mediaFiles: [...(prev.mediaFiles || []), file]
        }));

        setSnackbarMessage('Imagem do produto adicionada com sucesso!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } catch (error) {
        console.error('Erro ao carregar imagem do produto:', error);
        setSnackbarMessage('Não foi possível carregar a imagem do produto.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    }

    setOpenProductDialog(false);
  };

  const handleDateSelect = (date: Date) => {
    const now = new Date();
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);

    if (selectedDate < now) {
      setSnackbarMessage('Não é possível selecionar datas passadas!');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    const isSelected = selectedDates.some(d => isSameDay(d.date, date));
    
    if (isSelected) {
      setSelectedDates(selectedDates.filter(d => !isSameDay(d.date, date)));
    } else {
      setSelectedDates([...selectedDates, { date, times: [] }]);
      setSelectedDateForTime(date);
      setShowTimePicker(true);
    }
  };

  const handleTimeAdd = (time: Date) => {
    const now = new Date();
    
    // Verifica se o horário é posterior ao atual
    if (time <= now) {
      setSnackbarMessage('Selecione um horário posterior ao horário atual!');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    if (selectedDateForTime) {
      // Cria uma cópia das datas selecionadas
      const newSelectedDates = [...selectedDates];
      
      // Encontra o índice da data selecionada
      const dateIndex = newSelectedDates.findIndex(d => 
        isSameDay(d.date, selectedDateForTime)
      );
      
      if (dateIndex !== -1) {
        // Adiciona o horário à data existente
        newSelectedDates[dateIndex].times.push(time);
      }
      
      // Atualiza o estado com a nova lista
      setSelectedDates(newSelectedDates);
      
      // Feedback para o usuário
      setSnackbarMessage('Horário adicionado com sucesso!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Fecha o seletor de horário
      setShowTimePicker(false);
    }
  };

  const handleTimeRemove = (date: Date, timeIndex: number) => {
    console.log('Removendo horário:', timeIndex, 'da data:', date);
    
    setSelectedDates(prevDates => {
      const updatedDates = prevDates.map(d => 
        isSameDay(d.date, date)
          ? { ...d, times: d.times.filter((_, i) => i !== timeIndex) }
          : d
      );
      console.log('Datas atualizadas após remover horário:', updatedDates);
      return updatedDates;
    });
  };

  const handleDeletePost = async (postId: string) => {
    try {
      setPosts(prevPosts => prevPosts.filter(post => post.id !== postId));
    } catch (error) {
      console.error('Erro ao excluir postagem:', error);
    }
  };

  const handleEditPost = (post: Post) => {
    try {
      // Converter as datas de agendamento para objetos Date
      const schedules = post.schedules.map(schedule => ({
        ...schedule,
        startDate: ensureDate(schedule.startDate),
        endDate: ensureDate(schedule.endDate)
      }));
      
      // Preencher o formulário com os dados da postagem
      setNewPost({
        content: post.content || '',
        platforms: post.platforms || [],
        platformTypes: post.platformTypes || {},
        schedules: schedules,
        imageUrl: post.imageUrl || '',
        mediaFiles: post.mediaFiles || [],
        platformStatus: post.platformStatus || {},
        platformMedia: post.platformMedia || {},
        product: post.product,
        createdAt: post.createdAt || new Date()
      });
      
      // Configurar as datas selecionadas para o diálogo de datas
      const selectedDatesFromPost: SelectedDate[] = [];
      schedules.forEach(schedule => {
        const startDate = ensureDate(schedule.startDate);
        const existingDateIndex = selectedDatesFromPost.findIndex(d => 
          isSameDay(d.date, startDate)
        );
        
        if (existingDateIndex >= 0) {
          // Adicionar o horário a uma data existente
          selectedDatesFromPost[existingDateIndex].times.push(startDate);
        } else {
          // Adicionar uma nova data com o horário
          selectedDatesFromPost.push({
            date: startDate,
            times: [startDate]
          });
        }
      });
      
      setSelectedDates(selectedDatesFromPost);
      
      // Configurar os arquivos de mídia
      if (post.mediaFiles && post.mediaFiles.length > 0) {
        const newMediaFiles = post.mediaFiles
          .filter(file => file && file.type)
          .map(file => ({
            id: Math.random().toString(36).substr(2, 9),
            file,
            type: file.type.startsWith('image/') ? 'image' as const : 'video' as const,
            preview: URL.createObjectURL(file)
          }));
        setMediaFiles(newMediaFiles);
      } else {
        setMediaFiles([]);
      }
      
      // Remover a postagem original da lista
      setPosts(prevPosts => prevPosts.filter(p => p.id !== post.id));
      
      // Rolar para o topo da página para mostrar o formulário
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Feedback para o usuário
      setSnackbarMessage('Postagem carregada para edição. Faça as alterações e salve novamente.');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Erro ao editar postagem:', error);
      setSnackbarMessage('Erro ao editar postagem. Tente novamente.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const platforms = [
    { id: 'instagram', name: 'Instagram', icon: <InstagramIcon /> },
    { id: 'facebook', name: 'Facebook', icon: <FacebookIcon /> },
    { id: 'tiktok', name: 'TikTok', icon: <SmartToyIcon /> },
    { id: 'youtube', name: 'YouTube', icon: <YouTubeIcon /> },
  ];

  const postTypes = {
    instagram: ['feed', 'reels', 'story'],
    facebook: ['feed', 'reels', 'story'],
    tiktok: ['video'],
    youtube: ['video', 'shorts'],
  };

  const handleOpenDateDialog = () => {
    // Limpa as datas selecionadas anteriormente
    setSelectedDates([]);
    setOpenDateDialog(true);
  };

  const handleDateDialogConfirm = () => {
    // Verifica se há datas e horários selecionados
    if (selectedDates.length === 0) {
      setSnackbarMessage('Por favor, selecione pelo menos uma data para agendamento.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // Verifica se todas as datas selecionadas têm horários
    const hasEmptyTimes = selectedDates.some(d => d.times.length === 0);
    if (hasEmptyTimes) {
      setSnackbarMessage('Por favor, adicione horários para todas as datas selecionadas.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // Cria os agendamentos a partir das datas selecionadas
    const schedules: Schedule[] = [];
    selectedDates.forEach(selectedDate => {
      selectedDate.times.forEach(time => {
        schedules.push({
          startDate: time,
          endDate: new Date(time.getTime() + 3600000) // 1 hora de duração
        });
      });
    });

    // Atualiza o newPost com os agendamentos
    setNewPost(prev => ({
      ...prev,
      schedules: schedules
    }));

    // Fecha o diálogo
    setOpenDateDialog(false);

    // Feedback para o usuário
    setSnackbarMessage(`${schedules.length} horário(s) agendado(s) com sucesso!`);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  // Função para garantir que o objeto seja uma data válida
  const ensureDate = (date: any): Date => {
    if (date instanceof Date) {
      return date;
    }
    if (typeof date === 'string') {
      const parsed = new Date(date);
      return isNaN(parsed.getTime()) ? new Date() : parsed;
    }
    return new Date();
  };

  // Função para verificar falhas de publicação
  const checkFailedPosts = () => {
    posts.forEach(post => {
      Object.entries(post.platformStatus).forEach(([platform, status]) => {
        if (status.status === 'falha') {
          createNotification({
            title: `Falha na publicação`,
            message: `Falha na publicação para ${platform}.`,
            type: 'error'
          });
        }
      });
    });
  };

  // Verifica falhas a cada 5 minutos
  useEffect(() => {
    const interval = setInterval(checkFailedPosts, 300000);
    return () => clearInterval(interval);
  }, [posts]);

  return (
    <Box>
      {showPostForm && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Nova Postagem
            </Typography>
            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setOpenProductDialog(true)}
              >
                Buscar Produto
              </Button>
            </Box>
          </Box>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <RichTextEditor
                  value={newPost.content}
                  onChange={(value) => setNewPost({ ...newPost, content: value })}
                  placeholder="Digite o conteúdo da sua postagem..."
                />
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={isImprovingText ? <CircularProgress size={20} color="secondary" /> : <AutoFixHighIcon />}
                  fullWidth
                  sx={{ mt: 1 }}
                  onClick={handleImproveTextWithAI}
                  disabled={isImprovingText || !newPost.content}
                >
                  {isImprovingText ? 'Melhorando...' : 'Melhorar com IA'}
                </Button>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ height: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    Mídia
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <input
                      accept="image/*,video/*"
                      style={{ display: 'none' }}
                      id="media-upload"
                      type="file"
                      onChange={handleMediaUpload}
                    />
                    <label htmlFor="media-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<CloudUploadIcon />}
                        sx={{ mb: 2 }}
                        fullWidth
                      >
                        {mediaFiles.length > 0 ? 'Trocar Mídia' : 'Carregar Mídia'}
                      </Button>
                    </label>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Suporta imagens e vídeos (uma mídia por vez)
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {mediaFiles.map((media) => (
                      <Card key={media.id}>
                        <CardMedia
                          component={media.type === 'image' ? 'img' : 'video'}
                          height="200"
                          image={media.preview}
                          controls={!media.type.startsWith('image/')}
                          sx={{ objectFit: 'cover' }}
                        />
                        <CardActions>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleRemoveMedia(media.id)}
                            startIcon={<DeleteIcon />}
                            fullWidth
                          >
                            Remover
                          </Button>
                        </CardActions>
                      </Card>
                    ))}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                {showPlatformSelector && (
                  <FormControl fullWidth required>
                    <InputLabel id="platforms-label">Plataformas</InputLabel>
                    <Select
                      labelId="platforms-label"
                      multiple
                      value={newPost.platforms}
                      onChange={handlePlatformChange}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as string[]).map((platform) => (
                            <Chip
                              key={platform}
                              label={platform}
                              icon={
                                platform === 'Instagram' ? <InstagramIcon /> :
                                platform === 'Facebook' ? <FacebookIcon /> :
                                platform === 'TikTok' ? <SmartToyIcon /> :
                                platform === 'YouTube' ? <YouTubeIcon /> :
                                <ShareIcon />
                              }
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {platforms
                        .filter(platform => !disabledPlatforms.includes(platform.id))
                        .map((platform) => (
                          <MenuItem key={platform.id} value={platform.id}>
                            {platform.icon}
                            <Typography sx={{ ml: 1 }}>{platform.name}</Typography>
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Box display="flex" gap={2}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleOpenDateDialog}
                    startIcon={<ScheduleIcon />}
                  >
                    Data e Hora de Agendamento
                  </Button>
                  {selectedDates.length > 0 && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setSelectedDates([]);
                        setNewPost(prev => ({
                          ...prev,
                          schedules: []
                        }));
                        setSnackbarMessage('Datas e horários cancelados!');
                        setSnackbarSeverity('info');
                        setSnackbarOpen(true);
                      }}
                      startIcon={<CloseIcon />}
                    >
                      Cancelar Agendamento
                    </Button>
                  )}
                </Box>
                {newPost.schedules.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Datas e Horários Agendados:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {newPost.schedules.map((schedule, index) => (
                        <Chip
                          key={index}
                          label={schedule.startDate.toLocaleString('pt-BR')}
                          onDelete={() => handleRemoveSchedule(index)}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12}>
                <Button type="submit" variant="contained" color="primary" fullWidth>
                  Agendar Postagem
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      )}

      {showPostHistory && (
        <>
          {!showPostForm && (
            <Typography variant="h6" gutterBottom>
              Postagens Agendadas
            </Typography>
          )}
          <Grid container spacing={3}>
            {posts.map((post) => renderPostCard(post))}
          </Grid>
        </>
      )}

      <Dialog
        open={openProductDialog}
        onClose={() => setOpenProductDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Selecionar Produto</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Buscar produtos..."
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <List>
            {filteredProducts.map((product) => (
              <ListItem 
                key={product.id}
                button
                onClick={() => handleSelectProduct(product)}
                sx={{
                  borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                  '&:last-child': { borderBottom: 'none' }
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    variant="rounded"
                    src={product.imageUrl}
                    alt={product.name}
                    sx={{ width: 60, height: 60, mr: 2 }}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={product.name}
                  secondary={
                    <Box>
                      <Typography variant="body2">
                        Preço: R$ {product.price.toFixed(2)}
                      </Typography>
                      {product.description && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {product.description}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
            {filteredProducts.length === 0 && (
              <Typography color="text.secondary" textAlign="center" py={2}>
                Nenhum produto encontrado
              </Typography>
            )}
            {productsLoading && (
              <Typography color="text.secondary" textAlign="center" py={2}>
                Carregando produtos...
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenProductDialog(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Preview do Post
        </DialogTitle>
        <DialogContent>
          {previewPost && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Plataformas:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                {previewPost.platforms.map(platform => (
                  <Chip 
                    key={platform} 
                    label={platform} 
                    color="primary" 
                    variant="outlined"
                  />
                ))}
              </Box>

              {/* Preview do produto */}
              {previewPost.product && (
                <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: 'background.paper' }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Produto Associado:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    {previewPost.product.imageUrl && (
                      <Box 
                        sx={{ 
                          width: 120, 
                          height: 120, 
                          borderRadius: 1,
                          overflow: 'hidden',
                          flexShrink: 0,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                      >
                        <Box
                          component="img"
                          src={previewPost.product.imageUrl}
                          alt={previewPost.product.name}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0, py: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="h6" noWrap sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          {previewPost.product.name}
                        </Typography>
                        <Chip 
                          label="Produto" 
                          size="small" 
                          color="secondary" 
                          variant="outlined"
                          sx={{ flexShrink: 0, ml: 1 }}
                        />
                      </Box>
                      <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 'medium', mb: 1 }}>
                        R$ {previewPost.product.price.toFixed(2)}
                      </Typography>
                      {previewPost.product.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ 
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {previewPost.product.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Adiciona as opções de postagem selecionadas */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Opções de Postagem:
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {previewPost.platforms.map(platform => (
                    <Box key={platform} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {platform}:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {previewPost.platformTypes[platform]?.map(type => (
                          <Chip 
                            key={type}
                            label={type.charAt(0).toUpperCase() + type.slice(1)}
                            color="secondary"
                            size="small" 
                          />
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              <Grid container spacing={3}>
                {previewPost.platforms.map((platform, index) => (
                  <Grid item key={platform}>
                    <Box 
                      sx={{ 
                        mb: 4,
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        backgroundColor: '#f5f5f5',
                        position: 'relative'
                      }}
                    >
                      <Box sx={{ 
                        position: 'absolute', 
                        top: 8, 
                        right: 8, 
                        display: 'flex', 
                        gap: 1, 
                        zIndex: 1 
                      }}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*,video/*';
                            input.onchange = async (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0];
                              if (file) {
                                try {
                                  // Redimensiona a mídia para as dimensões da plataforma
                                  const resizedFile = await resizeMedia(file, platform);
                                  
                                  // Atualiza o previewPost com a nova mídia
                                  setPreviewPost(prev => {
                                    if (!prev) return null;
                                    return {
                                      ...prev,
                                      platformMedia: {
                                        ...prev.platformMedia,
                                        [platform]: resizedFile
                                      }
                                    };
                                  });

                                  // Atualiza a lista de posts
                                  setPosts(prevPosts => 
                                    prevPosts.map(post => {
                                      if (post.id === previewPost?.id) {
                                        return {
                                          ...post,
                                          platformMedia: {
                                            ...post.platformMedia,
                                            [platform]: resizedFile
                                          }
                                        };
                                      }
                                      return post;
                                    })
                                  );

                                  setSnackbarMessage('Mídia atualizada com sucesso!');
                                  setSnackbarSeverity('success');
                                  setSnackbarOpen(true);
                                } catch (error) {
                                  console.error('Erro ao redimensionar mídia:', error);
                                  setSnackbarMessage('Erro ao processar a mídia. Verifique as dimensões e tente novamente.');
                                  setSnackbarSeverity('error');
                                  setSnackbarOpen(true);
                                }
                              }
                            };
                            input.click();
                          }}
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 1)',
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (!previewPost) return;
                            
                            // Remove a mídia da plataforma
                            const platformMedia = previewPost.platformMedia || {};
                            const updatedPlatformMedia = { ...platformMedia };
                            delete updatedPlatformMedia[platform];
                            
                            // Remove os agendamentos da plataforma
                            const updatedPlatformStatus = { ...previewPost.platformStatus };
                            delete updatedPlatformStatus[platform];
                            
                            // Remove a plataforma da lista de plataformas
                            const updatedPlatforms = previewPost.platforms.filter(p => p !== platform);
                            
                            // Se não houver mais plataformas, fecha o preview
                            if (updatedPlatforms.length === 0) {
                              setShowPreview(false);
                              setPreviewPost(null);
                              return;
                            }
                            
                            // Atualiza a lista de posts
                            setPosts(prevPosts => 
                              prevPosts.map(post => {
                                if (post.id === previewPost.id) {
                                  // Remove a plataforma da lista de plataformas
                                  const updatedPlatforms = post.platforms.filter(p => p !== platform);
                                  
                                  // Cria um novo objeto platformMedia sem a plataforma removida
                                  const updatedPlatformMedia = Object.entries(post.platformMedia)
                                    .filter(([key]) => key !== platform)
                                    .reduce((acc, [key, value]) => ({
                                      ...acc,
                                      [key]: value
                                    }), {} as { [key: string]: File });
                                  
                                  // Cria um novo objeto platformStatus sem a plataforma removida
                                  const updatedPlatformStatus = Object.entries(post.platformStatus)
                                    .filter(([key]) => key !== platform)
                                    .reduce((acc, [key, value]) => ({
                                      ...acc,
                                      [key]: value
                                    }), {} as { [key: string]: { status: 'agendado' | 'publicado' | 'falha'; publishedAt?: Date; error?: string; whatsappNumber?: string; } });

                                  // Cria um novo objeto platformTypes sem a plataforma removida
                                  const updatedPlatformTypes = Object.entries(post.platformTypes)
                                    .filter(([key]) => key !== platform)
                                    .reduce((acc, [key, value]) => ({
                                      ...acc,
                                      [key]: value
                                    }), {} as { [key: string]: string[] });
                                  
                                  return {
                                    ...post,
                                    platforms: updatedPlatforms,
                                    platformMedia: updatedPlatformMedia,
                                    platformStatus: updatedPlatformStatus,
                                    platformTypes: updatedPlatformTypes
                                  };
                                }
                                return post;
                              })
                            );

                            setSnackbarMessage('Mídia e agendamento removidos com sucesso!');
                            setSnackbarSeverity('success');
                            setSnackbarOpen(true);

                            // Atualiza o previewPost
                            setPreviewPost({
                              ...previewPost,
                              platforms: updatedPlatforms,
                              platformMedia: updatedPlatformMedia,
                              platformStatus: updatedPlatformStatus,
                              platformTypes: previewPost.platformTypes
                            });
                          }}
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 1)',
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Preview para {platform}:
                      </Typography>
                      <Box
                        sx={{
                          ...getPlatformPreviewStyle(platform),
                          overflow: 'hidden',
                          position: 'relative',
                          boxShadow: '0 2px 12px rgba(0,0,0,0.1)'
                        }}
                      >
                        {/* Header da plataforma */}
                        <Box sx={getPlatformHeaderStyle(platform)}>
                          <Avatar 
                            sx={{ width: 32, height: 32 }}
                            src="/path-to-platform-logo.png"
                          >
                            {platform[0]}
                          </Avatar>
                          <Typography variant="subtitle2">
                            {platform} Preview
                          </Typography>
                        </Box>

                        {/* Conteúdo do post */}
                        <Box sx={{ 
                          position: 'relative', 
                          height: 'calc(100% - 54px)',
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          {previewPost.platformMedia?.[platform] && (
                            <Box
                              component={previewPost.platformMedia[platform].type.startsWith('image/') ? 'img' : 'video'}
                              src={URL.createObjectURL(previewPost.platformMedia[platform])}
                              controls={!previewPost.platformMedia[platform].type.startsWith('image/')}
                              sx={{
                                width: '100%',
                                height: platform.toLowerCase() === 'tiktok' ? 'calc(100% - 80px)' : 'auto',
                                maxHeight: platform.toLowerCase() === 'tiktok' ? 'none' : '60%',
                                objectFit: 'cover'
                              }}
                            />
                          )}
                          
                          <Box
                            sx={{
                              padding: 2,
                              background: platform.toLowerCase() === 'tiktok' 
                                ? 'rgba(0,0,0,0.8)'
                                : '#ffffff',
                              color: platform.toLowerCase() === 'tiktok' ? 'white' : 'inherit',
                              flex: 1,
                              overflow: 'auto'
                            }}
                          >
                            <Typography 
                              variant="body2" 
                              sx={{
                                fontSize: '13px',
                                lineHeight: 1.4,
                                maxHeight: platform.toLowerCase() === 'tiktok' ? '80px' : '120px',
                                overflow: 'auto',
                                mb: 1
                              }}
                            >
                              {previewPost.content}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Typography variant="subtitle1" gutterBottom>
                Períodos de Agendamento:
              </Typography>
              {previewPost.schedules.map((schedule, index) => (
                <Box key={index} sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    Período {index + 1}:
                  </Typography>
                      <Typography variant="body2" color="text.secondary">
                    Início: {schedule.startDate.toLocaleString('pt-BR')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                    Fim: {schedule.endDate.toLocaleString('pt-BR')}
                      </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreview(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDateDialog}
        onClose={() => setOpenDateDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Selecionar Datas e Horários</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Selecione as Datas
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                <DateCalendar
                  value={currentDate}
                  onChange={(date) => date && setCurrentDate(date)}
                  sx={{ width: '100%' }}
                  minDate={new Date()}
                  slots={{
                    day: (props) => {
                      const isSelected = selectedDates.some(d => isSameDay(d.date, props.day));
                      const isPast = props.day < new Date();
                      return (
                        <Badge
                          overlap="circular"
                          badgeContent={isSelected ? '✓' : undefined}
                          color="primary"
                        >
                          <PickersDay
                            {...props}
                            onClick={() => handleDateSelect(props.day)}
                            disabled={isPast}
                            sx={{
                              ...props.sx,
                              '&.Mui-selected': {
                                backgroundColor: 'primary.main',
                                color: 'primary.contrastText',
                              },
                            }}
                          />
                        </Badge>
                      );
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Datas Selecionadas
              </Typography>
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {selectedDates.map((selectedDate, index) => (
                  <ListItem key={index} divider>
                    <ListItemText
                      primary={selectedDate.date.toLocaleDateString('pt-BR')}
                      secondary={
                        <Box>
                          {selectedDate.times.map((time, timeIndex) => (
                            <Box key={timeIndex} display="flex" alignItems="center" gap={1} mb={1}>
                              <Typography variant="body2">
                                {time.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleTimeRemove(selectedDate.date, timeIndex)}
                                color="error"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              setSelectedDateForTime(selectedDate.date);
                              setShowTimePicker(true);
                            }}
                          >
                            Adicionar Horário
                          </Button>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDateDialog(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleDateDialogConfirm} 
            variant="contained" 
            color="primary"
            disabled={selectedDates.length === 0}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showTimePicker}
        onClose={() => setShowTimePicker(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Adicionar Horário para {selectedDateForTime?.toLocaleDateString('pt-BR')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DateTimePicker
                label="Selecione o Horário"
                value={new Date()}
                onChange={(date: Date | null) => {
                  if (date instanceof Date) {
                    handleTimeAdd(date);
                    setShowTimePicker(false);
                  }
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: selectedDateForTime?.toDateString() === new Date().toDateString()
                      ? 'Para o dia atual, selecione um horário posterior ao atual'
                      : 'Selecione um horário para agendamento'
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTimePicker(false)}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SocialPostScheduler; 